import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UserCircle, LockKey, CheckCircle, GoogleLogo } from "@phosphor-icons/react";

export default function Profile() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const isGoogleOnly = user ? user.has_password === false : false;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setDone(false);
    if (!cur) return setErr("Enter your current password");
    if (next.length < 8) return setErr("New password must be at least 8 characters");
    if (next !== confirm) return setErr("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      setCur(""); setNext(""); setConfirm("");
      setDone(true);
      toast.success("Password updated successfully");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not update password — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8" data-testid="profile-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 grid place-items-center">
          <UserCircle size={20} weight="fill" className="text-primary" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Account</p>
          <h1 className="font-display text-3xl font-black">Profile</h1>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="profile-details">
        <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">Your details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input value={user?.name || ""} readOnly disabled className="mt-1" data-testid="profile-name" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input value={user?.email || ""} readOnly disabled className="mt-1 font-mono text-xs" data-testid="profile-email" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Name can be changed under <span className="text-foreground">Settings</span>. Email is fixed to your login identity.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5" data-testid="profile-password-card">
        <div className="flex items-center gap-2 mb-4">
          <LockKey size={14} weight="fill" className="text-primary" />
          <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">Change password</p>
        </div>

        {isGoogleOnly ? (
          <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/30 p-4"
            data-testid="profile-google-note">
            <GoogleLogo size={18} weight="bold" className="text-muted-foreground mt-0.5" />
            <p className="text-sm text-muted-foreground">
              You signed in with Google, so there's no Tradelog password to change.
              Manage your password from your Google Account settings instead.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="profile-password-form">
            <div>
              <Label className="text-xs text-muted-foreground">Current password</Label>
              <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                className="mt-1" autoComplete="current-password" data-testid="current-password-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">New password</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                  className="mt-1" autoComplete="new-password" data-testid="new-password-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Confirm new password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1" autoComplete="new-password" data-testid="confirm-password-input" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Minimum 8 characters.</p>

            {err && (
              <p className="text-sm text-red-400" data-testid="password-error">{err}</p>
            )}
            {done && (
              <p className="text-sm text-emerald-400 flex items-center gap-1.5" data-testid="password-success">
                <CheckCircle size={14} weight="fill" /> Password updated successfully.
              </p>
            )}

            <Button type="submit" disabled={busy} data-testid="change-password-btn">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
